import React, { useEffect, useRef, useState } from 'react';
import { Theme, css } from '@emotion/react';
import { rem } from 'polished';
import { colors, mixins, spaces } from '../../core/styles/theme/default';
import styled from '@emotion/styled';
import { Link } from '../../primitives';
import SearchToolbar from '../../components/SearchToolbar';
import { useOutsideClick } from '../../core/utils/Hooks';
import { SearchToolbarProps } from '../SearchToolbar/SearchToolbar';

const StyledButton = styled(Link)(
  ({
    hamburgerActive,
    theme,
  }: {
    hamburgerActive: boolean;
    theme?: Theme;
  }) => css`
    padding: ${rem(12)} ${rem(8)};
    margin: ${rem(6)} ${rem(spaces.alpha)};
    color: ${colors.nightBlue900};
    font-size: ${rem(14)};

    svg {
      width: ${rem(24)};
      height: ${rem(24)};
      margin-left: ${rem(16)};
    }

    ${mixins.MQMax('medium')} {
      padding: ${rem(11)} ${rem(8)};
      margin: ${rem(4)} 0 ${rem(4)} ${rem(spaces.alpha)};

      > span {
        ${mixins.visuallyHidden};
      }
    }

    ${hamburgerActive &&
    css`
      color: ${colors.white};

      svg path {
        stroke: ${colors.white};
      }
    `}

    ${theme?.stylesExtension?.navSearch?.button(hamburgerActive)}
  `,
);

const StyledSearchToolbar = styled(SearchToolbar)(
  ({
    hamburgerActive,
    theme,
  }: {
    hamburgerActive: boolean;
    theme?: Theme;
  }) => css`
    display: none;

    ${theme?.stylesExtension?.navSearch?.searchToolbar(hamburgerActive)}
  `,
);

const StyledNavSearchWrapper = styled.div`
  display: flex;
`;

const StyledNavSearch = styled.div(
  ({ searchBarVisible }: { searchBarVisible: boolean }) =>
    css`
      display: inline-flex;
      vertical-align: top;

      ${mixins.MQ('medium')} {
        align-items: center;
        margin-top: ${rem(10)};
      }

      ${searchBarVisible &&
      css`
        ${StyledNavSearchWrapper} {
          display: block;
        }

        ${StyledSearchToolbar} {
          display: flex;
        }

        ${StyledButton} {
          display: none;
        }

        ${mixins.MQMax('medium')} {
          position: absolute;
          right: 72px;
          left: ${rem(spaces.gamma)};
          z-index: 10;

          ${StyledNavSearchWrapper} {
            width: 100%;
          }
        }
      `}
    `,
);

const NavSearch = ({
  searchSubmitText,
  selectAllLabel,
  hamburgerActive = false,
  formAction = '/search',
  searchSuggestionsFetch,
}: NavSearchProps) => {
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const searchToolbarRef = useRef<HTMLDivElement>(null);

  const toggleSearchBar = () => {
    setSearchBarVisible(!searchBarVisible);
  };

  useOutsideClick(searchToolbarRef, () => {
    if (searchBarVisible) {
      toggleSearchBar();
    }
  });

  useEffect(() => {
    if (searchBarVisible) {
      searchToolbarRef.current?.querySelector('input')?.focus();
    }
  }, [searchBarVisible]);

  return (
    <StyledNavSearch searchBarVisible={searchBarVisible}>
      <StyledNavSearchWrapper>
        <StyledButton
          linkText={searchSubmitText}
          iconAfter="EchIconMagnify"
          as="button"
          color={hamburgerActive ? 'light' : 'primary'}
          onClick={toggleSearchBar}
          hamburgerActive={hamburgerActive}
        />
        <div ref={searchToolbarRef}>
          <StyledSearchToolbar
            variant={'small'}
            action={formAction}
            filtersHidden
            id="HeaderSearchToolbar"
            selectAllLabel={selectAllLabel}
            hamburgerActive={hamburgerActive}
            fetchSuggestions={searchSuggestionsFetch}
            searchButtonText={searchSubmitText}
            inputLabel={searchSubmitText}
          />
        </div>
      </StyledNavSearchWrapper>
    </StyledNavSearch>
  );
};

export type NavSearchProps = {
  searchSubmitText: string;
  selectAllLabel: string;
  hamburgerActive: boolean;
  formAction?: string;
  searchSuggestionsFetch?: SearchToolbarProps['fetchSuggestions'];
};

export default NavSearch;
